import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { getTenantById } from '../services/tenantService';
import {
  getTenantStats,
  getTenantStaff,
  getTenantMenuItems,
} from '../services/adminRestaurantService';
import { ArrowLeft, AlertCircle, Loader, ShoppingCart, TrendingUp, Building2, Users, UtensilsCrossed } from 'lucide-react';
import type { Tenant } from 'foodhub-shared/types';

const RestaurantDetailsPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [staff, setStaff] = useState<any[]>([]);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!tenantId) return;

      try {
        setLoading(true);
        setError(null);

        const [tenantData, statsData, staffData, menuData] = await Promise.all([
          getTenantById(tenantId),
          getTenantStats(tenantId),
          getTenantStaff(tenantId),
          getTenantMenuItems(tenantId),
        ]);

        setTenant(tenantData);
        setStats(statsData);
        setStaff(staffData);
        setMenuItems(menuData);
      } catch (err: any) {
        console.error('Error loading restaurant details:', err);
        setError(err.message || 'Failed to load restaurant details');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tenantId]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="ml-3 text-gray-600">Loading restaurant details...</p>
        </div>
      </AdminLayout>
    );
  }

  if (!tenant) {
    return (
      <AdminLayout>
        <div className="p-8">
          <button
            onClick={() => navigate('/tenants')}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Restaurants</span>
          </button>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Restaurant not found</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <button
          onClick={() => navigate('/tenants')}
          className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Restaurants</span>
        </button>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{tenant.name}</h1>
          <p className="text-gray-600 mt-2">{tenant.email}</p>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Orders</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalOrders}</p>
                </div>
                <ShoppingCart className="w-8 h-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Revenue</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">RM {stats.totalRevenue.toFixed(2)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Tables</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalTables}</p>
                </div>
                <Building2 className="w-8 h-8 text-purple-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Staff</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalStaff}</p>
                </div>
                <Users className="w-8 h-8 text-orange-600" />
              </div>
            </div>
          </div>
        )}

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Staff Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
              <Users className="w-5 h-5 text-orange-600" />
              <span>Staff Members ({staff.length})</span>
            </h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {staff.length === 0 ? (
                <p className="text-gray-600 text-sm">No staff members</p>
              ) : (
                staff.slice(0, 5).map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium text-gray-900">{member.name}</p>
                      <p className="text-xs text-gray-600">{member.role}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${member.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {member.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Menu Items Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
              <UtensilsCrossed className="w-5 h-5 text-blue-600" />
              <span>Menu Items ({menuItems.length})</span>
            </h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {menuItems.length === 0 ? (
                <p className="text-gray-600 text-sm">No menu items</p>
              ) : (
                menuItems.slice(0, 5).map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-600">RM {item.price.toFixed(2)}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${item.available ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {item.available ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default RestaurantDetailsPage;

